/**
 * The emailed sign-in link's landing, `/auth/link/<token>` (ORDR-189).
 *
 * Modelled on paio-campaigns-ui's `auth/SigninLinkRoute.tsx`. `GET
 * ${API_BASE_URL}/auth/link/<token>` spends the single-use token and sets
 * `paio_sid`. WITH CREDENTIALS: without `include` the browser neither sends
 * nor stores the cookie, the call still answers 200, and the member of staff
 * lands signed in nowhere.
 *
 * ONE IN-FLIGHT VERIFY. StrictMode runs effects twice in development, and a
 * second GET would spend the token and report SIGNIN_LINK_ALREADY_USED for a
 * link tapped once. The PROMISE is kept, not a boolean: a boolean guard lets
 * the first run's cleanup discard the only in-flight answer and the screen
 * hangs on "Checking". Both runs await the same call; whichever is mounted
 * handles it.
 *
 * ON SUCCESS, exactly what `AuthCallback.tsx` does: replace the document with
 * the validated return path, so `OrderingContextProvider` mounts fresh against
 * the new session and the spent token stays out of the Back button.
 *
 * EVERY REFUSAL SHOWN IS ONE THE SERVER NAMED. The server's `detail` is shown
 * only for the six codes paio-auth documents; anything else gets a generic
 * sentence rather than an echo of whatever came back. A dropped connection is
 * NOT an expired link, so it gets its own message.
 *
 * Served outside `OrderingContextProvider` (see `Root.tsx`): there is no
 * session until the tap succeeds.
 */

import { useEffect, useRef, useState, type ReactElement } from "react";
import { API_BASE_URL, LOGIN_PATH } from "../auth/config.js";
import { takeLinkReturnPath } from "../auth/linkReturnPath.js";

export interface SigninLinkProps {
  /** The token from the address, undecoded (`signinLinkTokenFrom`). */
  readonly token: string;
  /** Injected for tests. Defaults to replacing the document. */
  readonly navigate?: (path: string) => void;
}

/** The refusals whose server `detail` is shown as given. */
const NAMED_REFUSALS: ReadonlySet<string> = new Set([
  "SIGNIN_LINK_INVALID",
  "SIGNIN_LINK_ALREADY_USED",
  "SIGNIN_LINK_LOCKED",
  "SIGNIN_LINK_EXPIRED",
  "SIGNIN_LINK_NO_ACCOUNT",
  "SIGNIN_LINK_NOT_PERMITTED",
]);

const GENERIC_REFUSAL = "This link cannot be used. Request a new one from the sign-in page.";

const UNREACHABLE = "We could not reach the server to check your link. Check your connection and try again.";

/** `null` when the session was minted, else the sentence to show. */
type TapOutcome = string | null;

async function spendToken(token: string): Promise<TapOutcome> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/link/${token}`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json", "X-Correlation-ID": crypto.randomUUID() },
    });
  } catch {
    return UNREACHABLE;
  }
  if (response.ok) {
    return null;
  }
  const body: unknown = await response.json().catch(() => null);
  const envelope = (body !== null && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const code = envelope["error_code"];
  const detail = envelope["detail"];
  if (typeof code === "string" && NAMED_REFUSALS.has(code) && typeof detail === "string" && detail.trim() !== "") {
    return detail;
  }
  return GENERIC_REFUSAL;
}

function replaceDocument(path: string): void {
  window.location.replace(path);
}

export function SigninLink({ token, navigate = replaceDocument }: SigninLinkProps): ReactElement {
  const [refusal, setRefusal] = useState<string | null>(null);
  const inflight = useRef<{ token: string; outcome: Promise<TapOutcome> } | null>(null);
  const navigated = useRef(false);

  useEffect(() => {
    if (inflight.current === null || inflight.current.token !== token) {
      inflight.current = { token, outcome: spendToken(token) };
    }
    let live = true;
    void inflight.current.outcome.then((outcome) => {
      if (!live) {
        return;
      }
      if (outcome !== null) {
        setRefusal(outcome);
        return;
      }
      if (!navigated.current) {
        navigated.current = true;
        navigate(takeLinkReturnPath());
      }
    });
    return () => {
      live = false;
    };
  }, [token, navigate]);

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <section className="w-full max-w-md rounded-2xl border border-(--color-border) bg-(--surface-elevated) p-8 text-center">
        <h1 className="mb-3 text-2xl font-semibold text-(--color-text-primary)">
          {refusal === null ? "Signing you in" : "This link did not sign you in"}
        </h1>
        {refusal === null ? (
          <p role="status" className="text-(--color-text-secondary)">
            Checking your link...
          </p>
        ) : (
          <>
            <p role="alert" className="mb-6 text-(--color-text-secondary)">
              {refusal}
            </p>
            <a href={LOGIN_PATH} className="font-medium text-(--color-primary-700) underline">
              Back to sign in
            </a>
          </>
        )}
      </section>
    </main>
  );
}
